import { Component, type ErrorInfo, type ReactNode } from 'react'

import { logger } from '@/utils/logger'

import './ErrorBoundary.css'

const log = logger.scoped('ErrorBoundary')

type Props = {
  children: ReactNode
  /** Custom fallback UI; the default renders a branded error screen. */
  fallback?: ReactNode
}

type State = {
  hasError: boolean
}

/**
 * Catches render-time exceptions below it in the tree (async code and event
 * handlers still need try/catch — React_TypeScript_Standards §6.6). The only
 * class component the standards permit.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    log.error('Unhandled render error', {
      error,
      componentStack: info.componentStack,
    })
  }

  handleRetry = (): void => {
    this.setState({ hasError: false })
  }

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children
    }

    if (this.props.fallback) {
      return this.props.fallback
    }

    return (
      <div className="errorboundary" role="alert">
        <h2>Something went wrong</h2>
        <p>
          The page hit an unexpected error. Try again — if it keeps happening,
          let the Stagehand team know.
        </p>
        <div className="errorboundary__actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={this.handleRetry}
          >
            Try again
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => window.location.reload()}
          >
            Reload the page
          </button>
        </div>
      </div>
    )
  }
}

export default ErrorBoundary
